import { useState } from 'react';
import {
  Controller,
  FieldError,
  FieldErrorsImpl,
  Merge,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { FiAlertTriangle } from 'react-icons/fi';
import InputGroup from 'react-bootstrap/InputGroup';
import { Alert, Form, Stack } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { zodResolver } from '@hookform/resolvers/zod';
import 'react-datepicker/dist/react-datepicker.css';
import CurrencyInput from 'react-currency-input-field';
import { z } from 'zod';
import { format } from 'date-fns';
import { PayRatePeriod } from '@th-types/payRatePeriod';
import * as jobApi from '@company/services/job/api';
import {
  shiftDefaultValues,
  getValidationSchema,
} from '@services/shift/shiftService';
import { ModalConfirm, ThLoading } from '@components/elements';
import { useJobShiftContext } from '@company/state/jobShiftContext';
import { useJobOptions } from '@company/state/useQueries/useJobOptions';
import DATE_FORMATS from '@constants/dateFormat';
import IModalConfirmModel from '@th-types/confirm-model.type';
import { ShiftStatus } from '@th-types/jobs.type';
import {
  createDateTimeFromDifferentDates,
  isSameDateOrAfter,
} from 'src/utils/DateUtils';
import useAlert from '@hooks/useAlert';
import { CreateShiftModalProps } from './CreateShiftModal';

// TODO: Move this component to company-frontend/components path
function CreateShiftMobileModal({
  job,
  handleClose,
  shift,
  successCloseModal,
}: CreateShiftModalProps) {
  const { showWarningAlert, alertMessage } = useAlert();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalConfirmModel, setModalConfirmModel] =
    useState<IModalConfirmModel>();

  const { refetch } = useJobShiftContext();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    getValues,
    setValue,
    reset,
  } = useForm<ValidationShiftSchema>({
    resolver: zodResolver(getValidationSchema()),
    defaultValues: shiftDefaultValues(job, shift),
  });

  const { fields } = useFieldArray({
    control,
    name: 'shifts',
  });

  const [isLoadingAction, setLoadingAction] = useState(false);
  const { jobOptions } = useJobOptions();
  const jobTitles = jobOptions?.jobTitles || [];

  const validationErrors =
    (errors.shifts as Merge<
      FieldError,
      Merge<
        FieldError,
        FieldErrorsImpl<{
          date: Date;
          position: number;
          payRate: number;
          payRatePeriod: NonNullable<'hourly' | 'daily'>;
          breakPaid: NonNullable<'yes' | 'no'>;
          openPositions: number;
          openBackups: number;
          startTime: Date;
          localizedStart: string;
          endTime: Date;
          localizedEnd: string;
        }>
      >
    >[]) ?? [];

  if (!job) {
    return <div />;
  }

  const processSuccessResult = async (message: string) => {
    reset();
    if (refetch) {
      await refetch();
    }
    successCloseModal(message);
  };

  const formatShiftValues = (data: ValidationShiftSchema) => {
    const formData: ValidationShiftSchema = { shifts: [] };
    data.shifts.forEach((item) => {
      const shiftItem = item;
      shiftItem.start = format(item.startTime, DATE_FORMATS.DATE_TIME_FORMAT);
      shiftItem.end = format(item.endTime, DATE_FORMATS.DATE_TIME_FORMAT);
      shiftItem.payRatePeriod =
        shiftItem.payRatePeriod === 'daily'
          ? PayRatePeriod.DAY
          : PayRatePeriod.HOUR;
      shiftItem.payRate = shiftItem.payRate.replace('$', '').trim();
      formData.shifts.push(shiftItem);
    });
    return formData;
  };

  const validationSchema = getValidationSchema(job.timezone);
  type ValidationShiftSchema = z.infer<typeof validationSchema>;

  const processResult = (success: boolean, resultMessage: string) => {
    if (success) {
      processSuccessResult(resultMessage);
      handleClose();
    } else {
      showWarningAlert(resultMessage);
    }
  };

  const updateShift = async (formData: any) => {
    if (shift) {
      try {
        let resultMessage = '';
        let success = false;
        const updateResult = await jobApi.updateJobShifts(
          job.id,
          shift.id,
          formData.shifts[0]
        );
        success = updateResult?.success;
        resultMessage = success
          ? updateResult.message
          : updateResult.errorMessage;

        processResult(success, resultMessage);
      } catch (err) {
        console.log('Occurred an error trying to update a job shift', err);
      } finally {
        setShowConfirmModal(false);
      }
    }
  };

  const setConfirmModalData = (formData: any) => {
    setModalConfirmModel({
      message: (
        <div className="text-center">
          <p>
            <b>Are you sure?</b>
          </p>
          <p>
            This is a confirmed job shift. The worker who has confirmed it, has
            only this one confirmed job shift. By this update action, the worker
            will be moved to offered status and will be needed a new accept to
            get hired back again
          </p>
        </div>
      ),
      titleMessage: (
        <div className="text-center">
          <FiAlertTriangle className="pb-1 me-2" />
          <span>Update shift</span>
        </div>
      ),
      confirmFunction: () => updateShift(formData),
      show: showConfirmModal,
    });
  };

  const createJobShifts = async (
    data: ValidationShiftSchema,
    formData: any
  ) => {
    let success = false;
    let resultMessage = '';
    const result = await jobApi.addJobShifts(job.id, formData.shifts);
    success = result?.results?.length > 0;
    resultMessage = success
      ? `Job shift${
          data.shifts.length > 1 || data.shifts[0].openPositions > 1 ? 's' : ''
        } successfully created`
      : result.message;
    processResult(success, resultMessage);
  };

  const onSubmit: SubmitHandler<ValidationShiftSchema> = async (
    data: ValidationShiftSchema
  ) => {
    try {
      setLoadingAction(true);
      const formData = formatShiftValues(data);
      if (shift) {
        if (shift.shiftStatus === ShiftStatus.CONFIRMED) {
          setConfirmModalData(formData);
          setShowConfirmModal(true);
        } else {
          await updateShift(formData);
        }
      } else {
        await createJobShifts(data, formData);
      }
    } catch (err) {
      console.log(`Occurred an error trying to create shifts`, err);
    } finally {
      setLoadingAction(false);
    }
  };

  const adjustDateTimes = (date: Date) => {
    getValues().shifts[0].date = date;
    getValues().shifts[0].date.setHours(0);
    getValues().shifts[0].date.setMinutes(0);
    getValues().shifts[0].date.setSeconds(0);
    if (getValues().shifts[0].startTime) {
      getValues().shifts[0].startTime.setDate(date.getDate());
      getValues().shifts[0].startTime.setMonth(date.getMonth());
      getValues().shifts[0].startTime.setFullYear(date.getFullYear());
    }
    if (getValues().shifts[0].endTime) {
      getValues().shifts[0].endTime.setDate(date.getDate());
      getValues().shifts[0].endTime.setMonth(date.getMonth());
      getValues().shifts[0].endTime.setFullYear(date.getFullYear());
    }
  };

  return (
    <Modal
      size="xl"
      show
      centered
      onHide={() => handleClose()}
      aria-labelledby="create-shift-mobile-modal"
      className="create-shift-modal-mobile"
    >
      <Modal.Header closeButton>
        <Modal.Title>{shift ? 'Update' : 'Create'} Shift</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingAction && <ThLoading />}
        <Form id="shift-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={1}>
            <Form.Group className="d-flex align-items-center">
              <Form.Label aria-label="start-date">Start date*</Form.Label>
              <InputGroup>
                <Controller
                  control={control}
                  {...register(`shifts.${0}.date`)}
                  name={`shifts.${0}.date`}
                  render={({ field }) => (
                    <DatePicker
                      id="date"
                      className={`form-control ${
                        validationErrors[0]?.date?.message
                          ? 'border-danger'
                          : ''
                      }`}
                      wrapperClassName="date-picker"
                      selected={field.value as unknown as Date}
                      placeholderText="MM/DD/YYYY"
                      formatWeekDay={(name) => name.substring(0, 1)}
                      onChange={(date) => {
                        field.onChange(date as Date);
                        if (date) {
                          adjustDateTimes(date);
                        }
                      }}
                      calendarStartDay={1}
                    />
                  )}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group className="d-flex align-items-center">
              <Form.Label aria-label="position">Position*</Form.Label>
              <Form.Select
                isInvalid={
                  errors?.shifts && !!errors.shifts[0]?.position?.message
                }
                {...register(`shifts.${0}.position`, {
                  valueAsNumber: true,
                })}
                className={`w-50 ${
                  validationErrors[0]?.position?.message ? 'is-invalid' : ''
                }`}
              >
                <option value={0}>Please select</option>
                {jobTitles &&
                  jobTitles.map((item) => {
                    return (
                      <option
                        key={item.id}
                        value={item.id}
                        selected={fields[0].position === item.id}
                      >
                        {item.name}
                      </option>
                    );
                  })}
              </Form.Select>
            </Form.Group>
            <Form.Group className="d-flex align-items-center">
              <Form.Label aria-label="wage">Wage*</Form.Label>
              <InputGroup>
                <CurrencyInput
                  {...register(`shifts.${0}.payRate`)}
                  name={`shifts.${0}.payRate`}
                  allowNegativeValue={false}
                  defaultValue={Number(fields[0].payRate.substring(2))}
                  className={`form-control ${
                    validationErrors[0]?.payRate?.message ? 'is-invalid' : ''
                  }`}
                  prefix="$ "
                  step={5}
                  min={1}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group className="d-flex align-items-center">
              <Form.Label aria-label="pay-rate-period">
                Hourly/Daily*
              </Form.Label>
              <Form.Select
                className="w-50"
                aria-label="Please select"
                isInvalid={!!validationErrors[0]?.payRatePeriod}
                {...register(`shifts.${0}.payRatePeriod`)}
              >
                <option hidden>Select</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
              </Form.Select>
            </Form.Group>
            {!shift && (
              <div>
                <Form.Group className="d-flex align-items-center">
                  <Form.Label aria-label="openings">Openings*</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>#</InputGroup.Text>
                    <Form.Control
                      type="text"
                      isInvalid={!!validationErrors[0]?.openPositions}
                      {...register(`shifts.${0}.openPositions`, {
                        valueAsNumber: true,
                      })}
                    />
                  </InputGroup>
                </Form.Group>
                <Form.Group className="d-flex align-items-center">
                  <Form.Label aria-label="backups">Backups*</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>#</InputGroup.Text>
                    <Form.Control
                      type="text"
                      isInvalid={!!validationErrors[0]?.openBackups}
                      {...register(`shifts.${0}.openBackups`, {
                        valueAsNumber: true,
                      })}
                    />
                  </InputGroup>
                </Form.Group>
              </div>
            )}
            <Form.Group className="d-flex align-items-center">
              <Form.Label aria-label="start-time">Start time*</Form.Label>
              <InputGroup>
                <Controller
                  control={control}
                  name={`shifts.${0}.startTime`}
                  render={({ field }) => (
                    <DatePicker
                      className="form-control"
                      selected={field.value as unknown as Date}
                      onChange={(date) => {
                        const newStartTime = createDateTimeFromDifferentDates(
                          getValues().shifts[0].date,
                          date!
                        );
                        field.onChange(newStartTime);
                        setValue(`shifts.${0}.startTime`, newStartTime);
                        setValue(
                          `shifts.${0}.isNextDay`,
                          isSameDateOrAfter(
                            getValues().shifts[0].startTime,
                            getValues().shifts[0].endTime
                          )
                        );
                      }}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="hh:mm aa"
                    />
                  )}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group className="d-flex align-items-center">
              <Form.Label aria-label="end-time">End time*</Form.Label>
              <InputGroup>
                <Controller
                  control={control}
                  name={`shifts.${0}.endTime`}
                  render={({ field }) => (
                    <DatePicker
                      className="form-control"
                      selected={field.value as unknown as Date}
                      onChange={(date) => {
                        const newEndTime = createDateTimeFromDifferentDates(
                          getValues().shifts[0].date,
                          date!
                        );
                        field.onChange(newEndTime);
                        setValue(`shifts.${0}.endTime`, newEndTime);
                        setValue(
                          `shifts.${0}.isNextDay`,
                          isSameDateOrAfter(
                            getValues().shifts[0].startTime,
                            getValues().shifts[0].endTime
                          )
                        );
                      }}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="hh:mm aa"
                    />
                  )}
                />
              </InputGroup>
            </Form.Group>
            <Form.Group className="d-flex align-items-center">
              <Form.Label aria-label="break-paid">Break Paid?*</Form.Label>
              <Form.Select
                className="w-50"
                isInvalid={!!validationErrors[0]?.breakPaid}
                {...register(`shifts.${0}.breakPaid`)}
              >
                <option hidden>Select</option>
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </Form.Select>
            </Form.Group>
          </Stack>
        </Form>
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <Button variant="light" onClick={handleClose}>
          CANCEL
        </Button>
        <Button variant="primary" form="shift-form" type="submit">
          {shift ? 'UPDATE' : 'CREATE'}
        </Button>
      </Modal.Footer>
      {showConfirmModal && modalConfirmModel && (
        <ModalConfirm
          modalConfirmModel={modalConfirmModel}
          setShowModal={setShowConfirmModal}
        />
      )}
      <Alert
        show={alertMessage.show}
        variant={alertMessage.variant}
        className="alert-fixed"
        style={{ width: '20rem' }}
      >
        <Alert.Heading>{alertMessage.message}</Alert.Heading>
      </Alert>
    </Modal>
  );
}

export default CreateShiftMobileModal;
